/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  useTheme,
  Paper,
  alpha,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TabIcon from '@mui/icons-material/Tab';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import {
  Layout,
  ReactGridLayout,
  useContainerWidth,
  verticalCompactor,
} from 'react-grid-layout';
import CanvasWorkspaceTabs from 'components/canvas/CanvasWorkspaceTabs';
import AddComponentsDialog from 'components/canvas/AddComponentsDialog';
import {
  CanvasComponent,
  CanvasContentId,
  CanvasContainer,
  CanvasWorkspace,
  SymbolGroup,
} from 'types/canvas';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import { SGTooltip } from 'components/core/SGTooltip';
import { AVAILABLE_CANVAS_COMPONENTS } from 'config/canvas';
import { CANVAS_COMPONENT_META } from 'config/canvasComponentCatalog';
import CanvasGammaLevels from 'components/canvas/components/CanvasGammaLevels';
import IndexLevels from './HomeTabs/IndexLevels';
import { ExpandableContentWrapper } from 'components';
import { EarningsCalendar } from 'components/home/EarningsCalendar';
import { MacroCalendar } from 'components/home/MacroCalendar';
import { ScannerID } from 'types';
import IndexMetrics from './HomeTabs/IndexMetrics';
import KeySuppResistanceStrikes from './HomeTabs/KeySuppResistanceStrikes';
import SGPropietaryLevels from './HomeTabs/SGPropietaryLevels';
import { OCCChart } from 'components/sentiment/OCC';
import AtSpotgamma from './HomeTabs/AtSpotgamma';
import ScannerTable from './HomeTabs/ScannerTable';
import CanvasGammaModelChart from 'components/canvas/components/CanvasGammaModelChart';
import CanvasFuturesChart from 'components/canvas/components/CanvasFuturesChart';
import CanvasComboStrikes from 'components/canvas/components/CanvasComboStrikes';
import CanvasFixedStrikeMatrix from 'components/canvas/components/CanvasFixedStrikeMatrix';
import CanvasTermStructure from 'components/canvas/components/CanvasTermStructure';
import CanvasHiroChart from 'components/canvas/components/CanvasHiroChart';
import CanvasVolSkew from 'components/canvas/components/CanvasVolSkew';
import CanvasVixChart from 'components/canvas/components/CanvasVixChart';
import CanvasEquitiesScanner from 'components/canvas/components/CanvasEquitiesScanner';
import CanvasFoundersNotes from 'components/canvas/components/CanvasFoundersNotes';
import CanvasCompass from 'components/canvas/components/CanvasCompass';
import CanvasCompositeView from 'components/canvas/components/CanvasCompositeView';
import CanvasSynthOIPutCallImpact from 'components/canvas/components/CanvasSynthOIPutCallImpact';
import CanvasTotalOIPutCallImpact from 'components/canvas/components/CanvasTotalOIPutCallImpact';
import CanvasFlowData from 'components/canvas/components/CanvasFlowData';
import CanvasTrace from 'components/canvas/components/CanvasTrace';
import CanvasExpConcentration from 'components/canvas/components/CanvasExpConcentration';
import CanvasZeroDTEEquityVol from 'components/canvas/components/CanvasZeroDTEEquityVol';
import CanvasEquityPCRatio from 'components/canvas/components/CanvasEquityPCRatio';
import CanvasEarningsChart from 'components/canvas/components/CanvasEarningsChart';
import { useCanvasAPI, ContainerInput } from 'hooks/canvas';
import { useSymbolGroups } from 'hooks/canvas/useSymbolGroups';
import { useRecoilValue } from 'recoil';
import { watchlistsState } from 'states';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import {
  CanvasGroupContext,
  ManageGroupsDialog,
} from 'components/canvas/groups';
import useToast from 'hooks/useToast';

const MAX_COMPONENTS_PER_CONTAINER = 5;
const LAYOUT_SYNC_DEBOUNCE_MS = 1_500;

// localStorage key for the last-active workspace id. Restored on mount so
// a refresh lands the user on the workspace they were looking at.
const LAST_ACTIVE_WORKSPACE_KEY = 'canvas-last-active-workspace-id';

const readLastActiveWorkspaceId = (): number | null => {
  try {
    const raw = window.localStorage.getItem(LAST_ACTIVE_WORKSPACE_KEY);
    if (raw == null) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeLastActiveWorkspaceId = (id: number): void => {
  try {
    window.localStorage.setItem(LAST_ACTIVE_WORKSPACE_KEY, String(id));
  } catch {
    /* localStorage unavailable (private mode, quota) — silently skip */
  }
};

// Grid + row constants used for size math. Keep aligned with the
// `<ReactGridLayout>` `gridConfig` below.
const GRID_COLS = 12;
const GRID_ROW_HEIGHT = 30;

// Below this width Canvas collapses to a single stacked column.
const MOBILE_STACK_BREAKPOINT_PX = 768;

/**
 * Minimum pixel size we want a tile to occupy when its first chart lands
 * in it. Empty tiles are tiny by design; growing the chart inside one
 * would feel cramped without enforcing a floor first. Translated into
 * grid cells via the measured grid `width`, with sensible clamps so we
 * never overshoot the column count or invent rows out of thin air.
 */
const MIN_TILE_PX_W = 600;
const MIN_TILE_PX_H = 360;

const getMinimumTileSize = (gridWidth: number): { w: number; h: number } => {
  if (gridWidth <= 0) {
    return { w: GRID_COLS, h: Math.ceil(MIN_TILE_PX_H / GRID_ROW_HEIGHT) };
  }
  const pxPerCol = gridWidth / GRID_COLS;
  const wCols = Math.min(GRID_COLS, Math.ceil(MIN_TILE_PX_W / pxPerCol));
  const hRows = Math.ceil(MIN_TILE_PX_H / GRID_ROW_HEIGHT);
  return { w: wCols, h: hRows };
};

/**
 * Strips client-only fields and prepares containers for the PUT sync endpoint.
 * Containers with id <= 0 (draft) have their id omitted so the DB assigns one.
 */
const serializeLayoutForSync = (layout: CanvasContainer[]): ContainerInput[] =>
  layout.map((c) => ({
    ...(c.id > 0 ? { id: c.id } : {}),
    workspace_id: c.workspace_id ?? 0,
    i: c.i,
    x: c.x,
    y: c.y,
    w: c.w,
    h: c.h,
    minW: c.minW,
    maxW: c.maxW,
    minH: c.minH,
    maxH: c.maxH,
    static: c.static,
    isDraggable: c.isDraggable,
    isResizable: c.isResizable,
    mode: c.mode,
  }));

const Canvas = () => {
  const { width, containerRef, mounted, measureWidth } = useContainerWidth();
  // Narrow screens collapse to one stacked column (edits aren't persisted, so
  // the desktop layout is preserved — see onLayoutChange).
  const isNarrow = width > 0 && width < MOBILE_STACK_BREAKPOINT_PX;
  const gridCols = isNarrow ? 1 : GRID_COLS;
  const theme = useTheme();
  const api = useCanvasAPI();
  const { openToast } = useToast();
  const watchlists = useRecoilValue(watchlistsState);
  const watchlistedSyms = React.useMemo(
    () => new Set(watchlists?.flatMap((w) => w.symbols) ?? []),
    [watchlists],
  );
  const [workspaces, setWorkspaces] = useState<CanvasWorkspace[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<number | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [containerToRemove, setContainerToRemove] = useState<string | null>(
    null,
  );
  const [componentDialogOpen, setComponentDialogOpen] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<string | null>(
    null,
  );
  const [activeTabByContainer, setActiveTabByContainer] = useState<{
    [containerId: string]: number;
  }>({});
  const [syncStatus, setSyncStatus] = useState<'syncing' | 'saved'>('saved');
  // Tracks the container currently being resized so we can swap its inner
  // chart for a lightweight placeholder during the drag — heavy charts
  // re-layout sluggishly otherwise.
  const [resizingContainerId, setResizingContainerId] = useState<string | null>(
    null,
  );
  // Anchor for the "Add Container" split-button dropdown (tabbed vs single).
  const [addContainerMenuAnchor, setAddContainerMenuAnchor] =
    useState<HTMLElement | null>(null);

  // Ref to track the debounced layout sync timer
  const layoutSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Wraps an async API call with syncing/saved status transitions. */
  const withSyncStatus = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      setSyncStatus('syncing');
      try {
        const result = await fn();
        setSyncStatus('saved');
        return result;
      } catch (e) {
        setSyncStatus('saved');
        throw e;
      }
    },
    [],
  );
  // Ref to track which workspaces have had their full layout fetched
  const fetchedWorkspaceIds = useRef<Set<number>>(new Set());

  const activeWorkspace = workspaces.find(
    (workspace) => workspace.id === activeWorkspaceId,
  );

  // Tally of how many of each contentId already live in the active workspace,
  // used by AddComponentsDialog (and below) to enforce per-type caps like
  // "max 2 Trace per workspace".
  const workspaceCountByContentId = React.useMemo(() => {
    const counts: Partial<Record<CanvasContentId, number>> = {};
    if (!activeWorkspace) {
      return counts;
    }
    for (const container of activeWorkspace.layout) {
      for (const comp of container.components ?? []) {
        counts[comp.contentId] = (counts[comp.contentId] ?? 0) + 1;
      }
    }
    return counts;
  }, [activeWorkspace]);

  // Symbol-sharing groups: state + mutation helpers + sym-broadcast lookup,
  // distributed to per-component badges and the manage dialog via context.
  const symbolGroups = useSymbolGroups({
    activeWorkspace,
    setWorkspaces,
    api,
    withSyncStatus,
    serializeLayout: (workspace) => serializeLayoutForSync(workspace.layout),
    onBeforeMutation: () => pushUndo(),
  });

  // Cancel any pending debounced sync
  const cancelPendingSync = useCallback(() => {
    if (layoutSyncTimerRef.current != null) {
      clearTimeout(layoutSyncTimerRef.current);
      layoutSyncTimerRef.current = null;
    }
  }, []);

  // ── Undo stack ─────────────────────────────────────────────────────────
  //
  // Bounded per-workspace history of layout + groups snapshots. We
  // snapshot *before* each layout/group mutation (drag/resize start,
  // container add/remove, group create/rename/recolor/delete/setMember).
  // Popping the top entry restores both via the existing
  // `syncWorkspaceLayout` PUT — one network round-trip covers everything.
  //
  // Scope intentionally excludes component add/delete and per-component
  // config changes — those go through separate endpoints with new DB ids
  // and would require a more elaborate replay strategy.
  const UNDO_LIMIT = 25;
  type Snapshot = { layout: CanvasContainer[]; groups: SymbolGroup[] };
  const undoStackRef = useRef<Snapshot[]>([]);
  const [undoDepth, setUndoDepth] = useState(0);

  const pushUndo = useCallback(() => {
    if (activeWorkspace == null) {
      return;
    }
    const snap: Snapshot = {
      layout: activeWorkspace.layout,
      groups: activeWorkspace.groups ?? [],
    };
    const next = [...undoStackRef.current, snap].slice(-UNDO_LIMIT);
    undoStackRef.current = next;
    setUndoDepth(next.length);
  }, [activeWorkspace]);

  const undo = useCallback(() => {
    if (activeWorkspace == null || undoStackRef.current.length === 0) {
      return;
    }
    const stack = undoStackRef.current;
    const snap = stack[stack.length - 1];
    undoStackRef.current = stack.slice(0, -1);
    setUndoDepth(undoStackRef.current.length);

    const workspaceId = activeWorkspace.id;
    setWorkspaces((prev) =>
      prev.map((ws) =>
        ws.id === workspaceId
          ? { ...ws, layout: snap.layout, groups: snap.groups }
          : ws,
      ),
    );

    cancelPendingSync();
    const serialized = serializeLayoutForSync(snap.layout);
    void withSyncStatus(() =>
      api.syncWorkspaceLayout(workspaceId, undefined, serialized, snap.groups),
    );
  }, [activeWorkspace, api, cancelPendingSync, withSyncStatus]);

  // Clear undo history when switching workspaces — different tabs have
  // independent edit histories.
  useEffect(() => {
    undoStackRef.current = [];
    setUndoDepth(0);
  }, [activeWorkspaceId]);

  /**
   * When a sync response replaces a workspace, container `i` values may change
   * (e.g. draft "-1709123456" becomes "42"). Migrate activeTabByContainer keys
   * so tab state isn't lost.
   */
  const reconcileActiveTabsAfterSync = useCallback(
    (oldLayout: CanvasContainer[], newLayout: CanvasContainer[]) => {
      setActiveTabByContainer((prev) => {
        const updated = { ...prev };
        for (const oldContainer of oldLayout) {
          const activeCompId = prev[oldContainer.i];
          if (activeCompId == null) {
            continue;
          }

          // Find the matching new container by checking if it contains the same active component
          const newContainer = newLayout.find((nc) =>
            nc.components?.some((comp) => comp.id === activeCompId),
          );

          if (newContainer && newContainer.i !== oldContainer.i) {
            // Migrate the key
            updated[newContainer.i] = activeCompId;
            delete updated[oldContainer.i];
          }
        }
        return updated;
      });
    },
    [],
  );

  // Debounced layout sync to backend
  const debouncedSyncLayout = useCallback(
    (workspaceId: number, layout: CanvasContainer[]) => {
      cancelPendingSync();
      layoutSyncTimerRef.current = setTimeout(() => {
        void withSyncStatus(() => {
          const serialized = serializeLayoutForSync(layout);
          return api.syncWorkspaceLayout(workspaceId, undefined, serialized);
        }).then((result) => {
          if (result) {
            reconcileActiveTabsAfterSync(layout, result.layout);
            setWorkspaces((prev) =>
              prev.map((ws) => (ws.id === workspaceId ? result : ws)),
            );
          }
        });
      }, LAYOUT_SYNC_DEBOUNCE_MS);
    },
    [api, cancelPendingSync, reconcileActiveTabsAfterSync, withSyncStatus],
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      cancelPendingSync();
    };
  }, [cancelPendingSync]);

  // Persist the active workspace id so refresh lands the user back on it.
  useEffect(() => {
    if (activeWorkspaceId != null) {
      writeLastActiveWorkspaceId(activeWorkspaceId);
    }
  }, [activeWorkspaceId]);

  // Initial load: fetch workspace list and active workspace details
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const list = await api.fetchWorkspaces();

      if (cancelled) {
        return;
      }

      if (list && list.length > 0) {
        // Set workspaces with empty layouts, then fetch active workspace details
        const initialWorkspaces: CanvasWorkspace[] = list.map((ws) => ({
          id: ws.id,
          name: ws.name,
          layout: [],
        }));
        setWorkspaces(initialWorkspaces);

        // Restore the user's last-active workspace if it still exists;
        // otherwise fall back to the first one in the list.
        const savedId = readLastActiveWorkspaceId();
        const initialId =
          savedId != null && list.some((ws) => ws.id === savedId)
            ? savedId
            : list[0].id;
        setActiveWorkspaceId(initialId);

        const fullWorkspace = await api.fetchWorkspace(initialId);
        if (cancelled) {
          return;
        }

        if (fullWorkspace) {
          fetchedWorkspaceIds.current.add(fullWorkspace.id);
          setWorkspaces((prev) =>
            prev.map((ws) => (ws.id === fullWorkspace.id ? fullWorkspace : ws)),
          );
        }
      } else {
        // No workspaces exist — create a default one
        const created = await api.createWorkspace('Workspace 1');
        if (cancelled) {
          return;
        }

        if (created) {
          const newWorkspace: CanvasWorkspace = {
            id: created.id,
            name: created.name,
            layout: 'layout' in created ? created.layout : [],
          };
          fetchedWorkspaceIds.current.add(newWorkspace.id);
          setWorkspaces([newWorkspace]);
          setActiveWorkspaceId(newWorkspace.id);
        }
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-measure container width once loading finishes and layout has settled
  useEffect(() => {
    if (!loading && mounted) {
      requestAnimationFrame(() => measureWidth());
    }
  }, [loading, mounted, measureWidth]);

  // Re-sync grid width on window resize so a stale-narrow width can't strand
  // the right of the grid as an un-droppable "dead zone".
  useEffect(() => {
    if (!mounted) {
      return;
    }
    let raf = 0;
    const resync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => measureWidth());
    };
    window.addEventListener('resize', resync);
    window.addEventListener('orientationchange', resync);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resync);
      window.removeEventListener('orientationchange', resync);
    };
  }, [mounted, measureWidth]);

  // Lazy-load workspace details when switching tabs
  useEffect(() => {
    if (
      activeWorkspaceId == null ||
      fetchedWorkspaceIds.current.has(activeWorkspaceId)
    ) {
      return;
    }

    let cancelled = false;

    const loadWorkspace = async () => {
      const fullWorkspace = await api.fetchWorkspace(activeWorkspaceId);
      if (cancelled || !fullWorkspace) {
        return;
      }

      fetchedWorkspaceIds.current.add(fullWorkspace.id);
      setWorkspaces((prev) =>
        prev.map((ws) => (ws.id === fullWorkspace.id ? fullWorkspace : ws)),
      );
    };

    void loadWorkspace();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId]);

  const onLayoutChange = (layout: CanvasContainer[]) => {
    if (!activeWorkspace) {
      return;
    }

    // Skip persistence while narrow so the clamped 1-col positions don't
    // overwrite the saved desktop 12-col layout.
    if (isNarrow) {
      return;
    }

    // Convert Layout[] to CanvasContainer[] by preserving components from existing containers.
    // Spread layoutItem AFTER existingContainer so RGL-computed position/size fields win.
    const updatedLayout: CanvasContainer[] = layout.map((layoutItem) => {
      const existingContainer = activeWorkspace.layout.find(
        (container) => container.i === layoutItem.i,
      );

      return {
        // Preserve id, workspace_id, components from existing container
        ...existingContainer,
        // Apply all RGL-computed layout fields (x, y, w, h, minW, minH, etc.)
        ...layoutItem,
        // Preserve existing components when layout changes
        components: existingContainer?.components || [],
      };
    });

    const updatedWorkspaces = workspaces.map((workspace) =>
      workspace.id === activeWorkspaceId
        ? { ...workspace, layout: updatedLayout }
        : workspace,
    );

    setWorkspaces(updatedWorkspaces);

    // Debounced sync to backend
    debouncedSyncLayout(activeWorkspace.id, updatedLayout);
  };

  const handleAddWorkspace = async (name: string) => {
    const created = await withSyncStatus(() => api.createWorkspace(name));
    if (!created) {
      return;
    }

    const newWorkspace: CanvasWorkspace = {
      id: created.id,
      name: created.name,
      layout: 'layout' in created ? created.layout : [],
    };
    fetchedWorkspaceIds.current.add(newWorkspace.id);
    setWorkspaces((prev) => [...prev, newWorkspace]);
    setActiveWorkspaceId(newWorkspace.id);
  };

  const handleEditWorkspaceName = async (id: number, newName: string) => {
    // Optimistic update
    setWorkspaces((prev) =>
      prev.map((workspace) =>
        workspace.id === id ? { ...workspace, name: newName } : workspace,
      ),
    );

    const workspace = workspaces.find((ws) => ws.id === id);
    if (!workspace) {
      return;
    }

    const serialized = serializeLayoutForSync(workspace.layout);
    const result = await withSyncStatus(() =>
      api.syncWorkspaceLayout(id, newName, serialized),
    );
    if (result) {
      setWorkspaces((prev) => prev.map((ws) => (ws.id === id ? result : ws)));
    }
  };

  const handleRemoveWorkspace = async (idToRemove: number) => {
    if (workspaces.length <= 1) {
      return;
    }

    // Cancel any pending debounced sync that could re-add the workspace
    cancelPendingSync();

    const prevWorkspaces = workspaces;

    // Optimistic update
    const updatedWorkspaces = workspaces.filter(
      (workspace) => workspace.id !== idToRemove,
    );
    setWorkspaces(updatedWorkspaces);

    if (activeWorkspaceId === idToRemove) {
      setActiveWorkspaceId(updatedWorkspaces[0].id);
    }

    const success = await withSyncStatus(() => api.deleteWorkspace(idToRemove));
    if (!success) {
      // Revert on failure
      setWorkspaces(prevWorkspaces);
    }
  };

  const handleAddCanvasContainer = (mode: 'tabbed' | 'single' = 'tabbed') => {
    if (!activeWorkspace) {
      return;
    }
    pushUndo();

    // New containers always claim the top-left slot. Push every existing
    // container down by the new tile's height so the vertical compactor
    // doesn't pull them back into a collision with the new (0,0) one.
    const draftId = -Date.now();
    const newContainerHeight = 6;
    const newContainer: CanvasContainer = {
      id: draftId,
      workspace_id: activeWorkspace.id,
      i: String(draftId),
      x: 0,
      y: 0,
      w: 4,
      h: newContainerHeight,
      mode,
      components: [],
    };

    const shiftedLayout = activeWorkspace.layout.map((c) => ({
      ...c,
      y: c.y + newContainerHeight,
    }));
    const updatedLayout = [newContainer, ...shiftedLayout];
    const updatedWorkspaces = workspaces.map((workspace) =>
      workspace.id === activeWorkspaceId
        ? { ...workspace, layout: updatedLayout }
        : workspace,
    );

    setWorkspaces(updatedWorkspaces);

    // Immediate sync to get a real DB ID for the new container
    cancelPendingSync();
    const serialized = serializeLayoutForSync(updatedLayout);
    void withSyncStatus(() =>
      api.syncWorkspaceLayout(activeWorkspace.id, undefined, serialized),
    ).then((result) => {
      if (result) {
        reconcileActiveTabsAfterSync(updatedLayout, result.layout);
        setWorkspaces((prev) =>
          prev.map((ws) => (ws.id === activeWorkspace.id ? result : ws)),
        );
      }
    });
  };

  const handleRemoveClick = (containerId: string, item: CanvasContainer) => {
    if (item.components && item.components.length > 0) {
      setContainerToRemove(containerId);
      setConfirmDialogOpen(true);
    } else {
      handleRemoveContainer(containerId);
    }
  };

  const handleConfirmRemove = () => {
    if (containerToRemove) {
      handleRemoveContainer(containerToRemove);
    }
    setConfirmDialogOpen(false);
    setContainerToRemove(null);
  };

  const handleRemoveContainer = (containerId: string) => {
    if (!activeWorkspace) {
      return;
    }
    pushUndo();

    const updatedLayout = activeWorkspace.layout.filter(
      (container) => container.i !== containerId,
    );

    const updatedWorkspaces = workspaces.map((workspace) =>
      workspace.id === activeWorkspaceId
        ? { ...workspace, layout: updatedLayout }
        : workspace,
    );

    setWorkspaces(updatedWorkspaces);

    // Remove active tab state for this container
    setActiveTabByContainer((prev) => {
      const { [containerId]: _, ...rest } = prev;
      return rest;
    });

    // Sync to backend (prunes missing containers)
    cancelPendingSync();
    const serialized = serializeLayoutForSync(updatedLayout);
    void withSyncStatus(() =>
      api.syncWorkspaceLayout(activeWorkspace.id, undefined, serialized),
    ).then((result) => {
      if (result) {
        reconcileActiveTabsAfterSync(updatedLayout, result.layout);
        setWorkspaces((prev) =>
          prev.map((ws) => (ws.id === activeWorkspace.id ? result : ws)),
        );
      }
    });
  };

  const handleOpenComponentDialog = (containerId: string) => {
    setSelectedContainer(containerId);
    setComponentDialogOpen(true);
  };

  const handleAddSelectedComponents = async (
    selectedContentIds: CanvasContentId[],
  ) => {
    if (
      !activeWorkspace ||
      !selectedContainer ||
      selectedContentIds.length === 0
    ) {
      return;
    }

    const container = activeWorkspace.layout.find(
      (item) => item.i === selectedContainer,
    );
    if (!container) {
      return;
    }

    const containerId = container.id;

    // Defense in depth: enforce per-type workspace caps so a stale dialog
    // (or future programmatic add path) can't blow past them.
    const runningCounts: Partial<Record<CanvasContentId, number>> = {
      ...workspaceCountByContentId,
    };
    const allowedContentIds: CanvasContentId[] = [];
    const skipped: CanvasContentId[] = [];
    for (const contentId of selectedContentIds) {
      const cap = CANVAS_COMPONENT_META[contentId]?.maxPerWorkspace;
      const next = (runningCounts[contentId] ?? 0) + 1;
      if (cap != null && next > cap) {
        skipped.push(contentId);
        continue;
      }
      runningCounts[contentId] = next;
      allowedContentIds.push(contentId);
    }
    if (skipped.length > 0) {
      openToast({
        message: `Workspace limit reached for ${skipped.length} component${
          skipped.length > 1 ? 's' : ''
        }.`,
        type: 'error',
      });
    }
    if (allowedContentIds.length === 0) {
      return;
    }

    // Add components via batch API call
    const componentsToAdd = allowedContentIds.map((contentId, index) => {
      const template = AVAILABLE_CANVAS_COMPONENTS[contentId];
      return {
        contentId: template.contentId,
        label: template.label,
        type: template.type,
        config: template.config,
        position_index: (container.components?.length ?? 0) + index,
      };
    });

    const addedComponents: CanvasComponent[] = await withSyncStatus(
      async () => {
        const results = await Promise.all(
          componentsToAdd.map((c) => api.addComponent(containerId, c)),
        );
        return results.filter((r): r is CanvasComponent => r != null);
      },
    );

    const failedCount = componentsToAdd.length - addedComponents.length;
    if (failedCount > 0) {
      openToast({
        message: `Failed to add ${failedCount} component${failedCount > 1 ? 's' : ''}. Please try again.`,
        type: 'error',
      });
    }

    if (addedComponents.length === 0) {
      return;
    }

    // If this is the very first component landing in an empty container,
    // grow it to a sensible minimum so the chart doesn't render into a
    // tiny placeholder-sized box. We only ever GROW (Math.max) so a user
    // who's manually sized the tile bigger isn't shrunk back down.
    const wasEmpty = (container.components ?? []).length === 0;
    const minSize = wasEmpty ? getMinimumTileSize(width) : null;
    const resized =
      minSize != null && (container.w < minSize.w || container.h < minSize.h);

    // Update local state with the DB-assigned components (and the bumped
    // size, when applicable).
    const updatedLayout = activeWorkspace.layout.map((item) =>
      item.i === selectedContainer
        ? {
            ...item,
            components: [...(item.components || []), ...addedComponents],
            ...(minSize != null
              ? {
                  w: Math.max(item.w, minSize.w),
                  h: Math.max(item.h, minSize.h),
                }
              : {}),
          }
        : item,
    );

    const updatedWorkspaces = workspaces.map((workspace) =>
      workspace.id === activeWorkspaceId
        ? { ...workspace, layout: updatedLayout }
        : workspace,
    );

    setWorkspaces(updatedWorkspaces);

    // Persist the resize via the existing layout-sync path so refresh
    // reopens the tile at its grown size, not the original 4×6.
    if (resized) {
      cancelPendingSync();
      const serialized = serializeLayoutForSync(updatedLayout);
      void withSyncStatus(() =>
        api.syncWorkspaceLayout(activeWorkspace.id, undefined, serialized),
      );
    }

    // Always switch to the first newly added component
    setActiveTabByContainer((prev) => ({
      ...prev,
      [selectedContainer]: addedComponents[0].id,
    }));

    setComponentDialogOpen(false);
    setSelectedContainer(null);
  };

  const handleTabChange = (containerId: string, newValue: string) => {
    if (newValue === 'add-component') {
      handleOpenComponentDialog(containerId);
      return;
    }
    setActiveTabByContainer((prev) => ({
      ...prev,
      [containerId]: Number(newValue),
    }));
  };

  const handleRemoveComponent = (containerId: string, componentId: number) => {
    if (activeWorkspace == null) {
      return;
    }

    const container = activeWorkspace.layout.find(
      (item) => item.i === containerId,
    );
    if (container == null) {
      return;
    }

    const updatedLayout = activeWorkspace.layout.map((item) => {
      if (item.i === containerId) {
        const updatedComponents =
          item.components?.filter(
            (component) => component.id !== componentId,
          ) || [];

        // Adjust active tab if necessary
        const currentActiveTabId = activeTabByContainer[containerId];
        if (currentActiveTabId === componentId) {
          if (updatedComponents.length > 0) {
            setActiveTabByContainer((prev) => ({
              ...prev,
              [containerId]: updatedComponents[0].id,
            }));
          } else {
            setActiveTabByContainer((prev) => {
              const { [containerId]: _, ...rest } = prev;
              return rest;
            });
          }
        }

        return {
          ...item,
          components: updatedComponents,
        };
      }
      return item;
    });

    const updatedWorkspaces = workspaces.map((workspace) =>
      workspace.id === activeWorkspaceId
        ? { ...workspace, layout: updatedLayout }
        : workspace,
    );

    setWorkspaces(updatedWorkspaces);

    // Defense in depth — backend reconciles the workspace's groups when a
    // component is deleted, but stripping locally avoids a brief stale
    // chip render before the next workspace fetch arrives.
    symbolGroups.stripComponentLocally(componentId);

    // Delete component via API
    void withSyncStatus(() => api.deleteComponent(container.id, componentId));
  };

  const handleComponentSingleSymChange = (componentId: number, sym: string) => {
    if (activeWorkspace == null) {
      return;
    }

    // If the component belongs to a symbol-sharing group, broadcast the
    // sym change to every member. Otherwise it's a single-component update
    // (the standard behavior). Membership lookup is the only reason this
    // function is now group-aware — no behavior change for ungrouped
    // components.
    const group = symbolGroups.groupForComponent(componentId);
    const targetIds = new Set(group?.componentIds ?? [componentId]);

    // Walk the layout once, applying the new sym to every targeted member
    // and collecting (container, component) tuples we need to PUT.
    const broadcastTargets: Array<{
      container: CanvasContainer;
      component: CanvasComponent;
    }> = [];

    const updatedLayout = activeWorkspace.layout.map((item) => {
      if (item.components == null) {
        return item;
      }
      let containerChanged = false;
      const updatedComponents = item.components.map((comp) => {
        if (!targetIds.has(comp.id)) {
          return comp;
        }
        // Skip group members whose chart can't render this sym (e.g.
        // broadcasting AAPL to a Combo Strikes member that's indices-only).
        // The component keeps its previous sym instead of receiving invalid
        // data. The originating component always passes — its dropdown is
        // what produced the new sym in the first place.
        const supported = CANVAS_COMPONENT_META[comp.contentId]?.supportedSyms;
        if (supported != null && !supported.includes(sym)) {
          return comp;
        }
        containerChanged = true;
        const updated = { ...comp, config: { ...comp.config, sym } };
        broadcastTargets.push({ container: item, component: updated });
        return updated;
      });
      return containerChanged
        ? { ...item, components: updatedComponents }
        : item;
    });

    if (broadcastTargets.length === 0) {
      return;
    }

    setWorkspaces((prev) =>
      prev.map((workspace) =>
        workspace.id === activeWorkspace.id
          ? { ...workspace, layout: updatedLayout }
          : workspace,
      ),
    );

    // Persist each member in parallel. withSyncStatus shows the existing
    // syncing → saved indicator while the swarm of PUTs lands.
    void withSyncStatus(() =>
      Promise.all(
        broadcastTargets.map(({ container, component }) =>
          api.updateComponent(container.id, component.id, {
            label: component.label,
            config: component.config,
          }),
        ),
      ),
    );
  };

  const handleComponentConfigChange = (
    containerId: string,
    componentId: number,
    configPatch: Record<string, unknown>,
  ) => {
    if (activeWorkspace == null) {
      return;
    }

    const container = activeWorkspace.layout.find(
      (item) => item.i === containerId,
    );
    if (container == null) {
      return;
    }

    const component = container.components?.find((c) => c.id === componentId);
    if (component == null) {
      return;
    }

    const updatedConfig = { ...component.config, ...configPatch };

    const updatedLayout = activeWorkspace.layout.map((item) => {
      if (item.i === containerId) {
        return {
          ...item,
          components: item.components?.map((comp) => {
            if (comp.id === componentId) {
              return {
                ...comp,
                config: updatedConfig,
              };
            }
            return comp;
          }),
        };
      }
      return item;
    });

    const updatedWorkspaces = workspaces.map((workspace) =>
      workspace.id === activeWorkspace.id
        ? { ...workspace, layout: updatedLayout }
        : workspace,
    );

    setWorkspaces(updatedWorkspaces);

    void withSyncStatus(() =>
      api.updateComponent(container.id, componentId, {
        label: component.label,
        config: updatedConfig,
      }),
    );
  };

  /**
   * Returns the effective active component ID (number) for a container,
   * falling back to the first component's ID.
   */
  const getEffectiveActiveId = (
    containerId: string,
    components: CanvasComponent[],
  ): number | null => {
    if (components == null || components.length === 0) {
      return null;
    }
    const stored = activeTabByContainer[containerId];
    if (stored != null && components.some((c) => c.id === stored)) {
      return stored;
    }
    return components[0].id;
  };

  const getActiveComponent = (containerId: string): CanvasComponent | null => {
    const container = activeWorkspace?.layout.find(
      (item) => item.i === containerId,
    );
    if (container?.components?.length == null) {
      return null;
    }

    const activeId = getEffectiveActiveId(containerId, container.components);
    return container.components.find((c) => c.id === activeId) ?? null;
  };

  const getActiveComponentValue = (
    containerId: string,
    componentId: number,
  ): React.ReactNode => {
    const container = activeWorkspace?.layout.find(
      (item) => item.i === containerId,
    );
    if (container?.components?.length == null) {
      return null;
    }

    const component = container.components.find(
      (comp) => comp.id === componentId,
    );
    if (component == null) {
      return null;
    }

    switch (component.contentId) {
      case CanvasContentId.GAMMA_LEVELS_CHART:
        return (
          <CanvasGammaLevels
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
          />
        );
      case CanvasContentId.GAMMA_MODEL_CHART:
        return (
          <CanvasGammaModelChart
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
          />
        );
      case CanvasContentId.LIVE_PRICES_CHART:
        return (
          <CanvasFuturesChart
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
          />
        );
      case CanvasContentId.COMBO_STRIKES:
        return (
          <CanvasComboStrikes
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
          />
        );
      case CanvasContentId.FIXED_STRIKE_MATRIX:
        return (
          <CanvasFixedStrikeMatrix
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            config={component.config}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.TERM_STRUCTURE:
        return (
          <CanvasTermStructure
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            config={component.config}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.HIRO_CHART:
        return (
          <CanvasHiroChart
            id={component.id}
            config={component.config}
            onConfigChange={(patch: Record<string, unknown>) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.VOL_SKEW:
        return (
          <CanvasVolSkew
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            config={component.config}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.INDEX_LEVELS:
        return <IndexLevels />;
      case CanvasContentId.SG_LEVELS:
        return <SGPropietaryLevels />;
      case CanvasContentId.INDEX_METRICS:
        return <IndexMetrics />;
      case CanvasContentId.SUPP_RESISTANCE_LEVELS:
        return <KeySuppResistanceStrikes />;
      case CanvasContentId.MACRO_ECONOMIC_CALENDAR:
        return <MacroCalendar />;
      case CanvasContentId.EARNINGS:
        return <EarningsCalendar />;
      case CanvasContentId.EARNINGS_CHART:
        return <CanvasEarningsChart id={component.id} />;
      case CanvasContentId.SCANNER_SQUEEZE_CANDIDATES:
        return (
          <ScannerTable
            scanner={ScannerID.SQUEEZE}
            watchlistedSyms={watchlistedSyms}
          />
        );
      case CanvasContentId.SCANNER_VRP_CANDIDATES:
        return (
          <ScannerTable
            scanner={ScannerID.VOL_RISK_PREMIUM}
            watchlistedSyms={watchlistedSyms}
          />
        );
      case CanvasContentId.EQUITIES_SCANNER:
        return (
          <CanvasEquitiesScanner
            config={component.config}
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.AT_SPOTGAMMA:
        return <AtSpotgamma />;
      case CanvasContentId.OCC_CHART:
        return (
          <ExpandableContentWrapper
            type={CanvasContentId.OCC_CHART}
            styleOverrides={{
              p: 1,
              height: '100%',
            }}
          >
            <OCCChart />
          </ExpandableContentWrapper>
        );
      case CanvasContentId.ZERO_DTE_EQUITY_VOL_CHART:
        return (
          <CanvasZeroDTEEquityVol
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
          />
        );
      case CanvasContentId.EQUITY_PUT_CALL_RATIO_CHART:
        return <CanvasEquityPCRatio />;
      case CanvasContentId.EXP_CONCENTRATION_CHART:
        return (
          <CanvasExpConcentration
            config={component.config}
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.VIX_CHART:
        return (
          <CanvasVixChart
            id={component.id}
            config={component.config}
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.FOUNDERS_NOTES:
        return <CanvasFoundersNotes />;
      case CanvasContentId.STRATEGY_COMPASS:
        return (
          <CanvasCompass
            id={component.id}
            config={component.config}
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.COMPOSITE_VIEW_CHART:
        return (
          <CanvasCompositeView
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            config={component.config}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.SYNTH_COMPOSITE_VIEW_CHART:
        return (
          <CanvasCompositeView
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            config={component.config}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
            isSynthOI
          />
        );
      case CanvasContentId.SYNTH_OI_PUT_CALL_IMPACT_CHART:
        return (
          <CanvasSynthOIPutCallImpact
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            config={component.config}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.TOTAL_OI_PUT_CALL_IMPACT_CHART:
        return (
          <CanvasTotalOIPutCallImpact
            id={component.id}
            sym={component.config.sym ?? 'SPX'}
            config={component.config}
            onSymChange={(sym) =>
              handleComponentSingleSymChange(componentId, sym)
            }
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.FLOW_DATA:
        return (
          <CanvasFlowData
            id={component.id}
            config={component.config}
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      case CanvasContentId.TRACE:
        return (
          <CanvasTrace
            id={component.id}
            config={component.config}
            onConfigChange={(patch) =>
              handleComponentConfigChange(containerId, componentId, patch)
            }
          />
        );
      default:
        return <Typography>No component found</Typography>;
    }
  };

  if (loading) {
    return (
      <Stack
        sx={{
          height: 'calc(100vh - 205px)',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <CanvasGroupContext.Provider value={symbolGroups}>
      <Stack sx={{ paddingBottom: '60px' }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            // Zero horizontal padding so the workspace dropdown sits snug
            // against the bar's left edge. Buttons own their own padding.
            pl: 0,
            pr: 0,
            py: 0,
            minHeight: 44,
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
            backdropFilter: 'blur(6px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 1.5,
            boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.04)}`,
            overflow: 'hidden',
            // Every button stretches edge-to-edge so they read as
            // built-in toolbar segments instead of floating pills.
            '& > .MuiButtonBase-root, & > span > .MuiButtonBase-root': {
              height: 'auto',
              alignSelf: 'stretch',
              my: 0,
              borderRadius: 0,
              border: 'none',
            },
          }}
        >
          <CanvasWorkspaceTabs
            workspaces={workspaces}
            activeWorkspaceId={activeWorkspaceId ?? 0}
            setActiveWorkspaceId={setActiveWorkspaceId}
            handleRemoveWorkspace={(id) => void handleRemoveWorkspace(id)}
            handleAddWorkspace={(name) => void handleAddWorkspace(name)}
            handleEditWorkspaceName={(id, name) =>
              void handleEditWorkspaceName(id, name)
            }
          />

          {/* Visual divider between workspace and action buttons */}
          <Box
            sx={{
              alignSelf: 'stretch',
              width: '1px',
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            }}
          />

          <SGTooltip title="Add a new container">
            <span>
              <Button
                onClick={(e) => setAddContainerMenuAnchor(e.currentTarget)}
                disabled={!activeWorkspace}
                startIcon={<AddIcon sx={{ fontSize: { xs: 16, md: 18 } }} />}
                endIcon={
                  <ArrowDropDownIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                }
                sx={{
                  flexShrink: 0,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: 12, md: 13 },
                  letterSpacing: 0.2,
                  minHeight: 44,
                  px: { xs: 2, md: 4 },
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                  },
                  '& .label-text': {
                    display: { xs: 'none', sm: 'inline' },
                  },
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0, sm: 2 },
                  },
                  '& .MuiButton-endIcon': {
                    ml: { xs: 0, sm: 0.5 },
                  },
                }}
              >
                <Box component="span" className="label-text">
                  Add Container
                </Box>
              </Button>
            </span>
          </SGTooltip>
          <Menu
            anchorEl={addContainerMenuAnchor}
            open={Boolean(addContainerMenuAnchor)}
            onClose={() => setAddContainerMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 1.5,
                  mt: 0.5,
                  minWidth: 260,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                  backgroundImage: 'none',
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setAddContainerMenuAnchor(null);
                handleAddCanvasContainer('tabbed');
              }}
              sx={{ py: 1.25, alignItems: 'flex-start' }}
            >
              <ListItemIcon sx={{ mt: '2px' }}>
                <TabIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Tabbed container"
                secondary="Holds up to 5 components, switched via a tab strip."
                primaryTypographyProps={{
                  fontSize: 13,
                  fontWeight: 600,
                }}
                secondaryTypographyProps={{
                  fontSize: 11,
                  sx: { whiteSpace: 'normal' },
                }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAddContainerMenuAnchor(null);
                handleAddCanvasContainer('single');
              }}
              sx={{ py: 1.25, alignItems: 'flex-start' }}
            >
              <ListItemIcon sx={{ mt: '2px' }}>
                <WebAssetIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Single component"
                secondary="Just one item, no tab strip — maximum visibility."
                primaryTypographyProps={{
                  fontSize: 13,
                  fontWeight: 600,
                }}
                secondaryTypographyProps={{
                  fontSize: 11,
                  sx: { whiteSpace: 'normal' },
                }}
              />
            </MenuItem>
          </Menu>

          {/* Divider between action buttons */}
          <Box
            sx={{
              alignSelf: 'stretch',
              width: '1px',
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            }}
          />

          <SGTooltip
            title={
              symbolGroups.groups.length === 0
                ? 'Create symbol groups to keep multiple components in sync'
                : `Manage symbol groups (${symbolGroups.groups.length})`
            }
          >
            <Button
              onClick={symbolGroups.openManageDialog}
              startIcon={
                <WorkspacesIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
              }
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: 12, md: 13 },
                letterSpacing: 0.2,
                minHeight: 44,
                px: { xs: 2, md: 4 },
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
                '& .label-text': {
                  display: { xs: 'none', sm: 'inline' },
                },
                '& .MuiButton-startIcon': {
                  mr: { xs: 0, sm: 2 },
                },
              }}
              aria-label="Manage symbol groups"
            >
              <Box component="span" className="label-text">
                Groups
              </Box>
            </Button>
          </SGTooltip>

          {/* Divider between action buttons */}
          <Box
            sx={{
              alignSelf: 'stretch',
              width: '1px',
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            }}
          />

          {/* Pushes the right cluster to the far edge */}
          <Box sx={{ flex: 1 }} />

          {/* Right cluster: Undo + Saved indicator, with a divider
              before Undo so they read as a distinct status group. */}
          <Box
            sx={{
              alignSelf: 'stretch',
              width: '1px',
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            }}
          />
          <SGTooltip
            title={
              undoDepth > 0
                ? 'Undo last change. Covers layout moves, resizes, container add/remove, and group changes.'
                : 'Nothing to undo'
            }
          >
            <span>
              <IconButton
                onClick={undo}
                disabled={undoDepth === 0}
                aria-label="Undo last change"
                sx={{
                  flexShrink: 0,
                  width: 44,
                  borderRadius: 0,
                  color: theme.palette.text.secondary,
                  minHeight: 44,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <UndoIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </SGTooltip>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexShrink: 0,
              minHeight: 44,
              px: { xs: 2, md: 4 },
              whiteSpace: 'nowrap',
              color:
                syncStatus === 'syncing'
                  ? theme.palette.warning.main
                  : theme.palette.success.main,
              transition: 'color 0.2s ease',
            }}
          >
            {syncStatus === 'syncing' ? (
              <CloudSyncOutlinedIcon
                sx={{
                  fontSize: 14,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 0.5 },
                    '50%': { opacity: 1 },
                  },
                }}
              />
            ) : (
              <CloudDoneOutlinedIcon sx={{ fontSize: 14 }} />
            )}
            <Typography
              variant="caption"
              sx={{
                fontSize: 11,
                fontWeight: 500,
                color: 'inherit',
                display: { xs: 'none', sm: 'inline' },
              }}
            >
              {syncStatus === 'syncing' ? 'Saving' : 'Saved'}
            </Typography>
          </Box>
        </Paper>

        <div
          ref={containerRef as React.RefObject<HTMLDivElement>}
          style={{ position: 'relative', minHeight: '500px' }}
        >
          {mounted && activeWorkspace && (
            <>
              <ReactGridLayout
                // Remount on breakpoint cross so the grid re-stacks/expands.
                key={`${activeWorkspace.id}-${gridCols}`}
                dragConfig={{
                  cancel: '.canvas-no-drag',
                }}
                resizeConfig={{
                  handles: ['n', 'e', 's', 'w', 'ne', 'nw', 'se', 'sw'],
                }}
                layout={activeWorkspace.layout}
                width={width}
                compactor={verticalCompactor}
                gridConfig={{
                  cols: gridCols,
                  rowHeight: GRID_ROW_HEIGHT,
                  margin: [5, 5],
                }}
                onLayoutChange={(layout: Layout) =>
                  onLayoutChange(layout as CanvasContainer[])
                }
                onDragStart={() => {
                  pushUndo();
                }}
                onResizeStart={(_layout, _oldItem, newItem) => {
                  pushUndo();
                  setResizingContainerId(newItem?.i ?? null);
                }}
                onResizeStop={() => {
                  setResizingContainerId(null);
                }}
                style={{
                  minHeight: 'calc(100vh - 205px)',
                  position: 'relative',
                  zIndex: 0,
                  backgroundImage: `
                  linear-gradient(${alpha(theme.palette.primary.main, 0.15)} 1px, transparent 1px),
                  linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.15)} 1px, transparent 1px)
                `,
                  backgroundSize: '30px 30px',
                }}
                className="canvas-container"
              >
                {activeWorkspace.layout.map((item: CanvasContainer) => {
                  const activeTabId = getEffectiveActiveId(
                    item.i,
                    item.components,
                  );
                  const isSingleMode = item.mode === 'single';
                  return (
                    <Paper
                      key={item.i}
                      elevation={1}
                      sx={{
                        zIndex: 1,
                        padding: item?.components?.length > 0 ? 1.25 : 1.5,
                        position: 'relative',
                        background: theme.palette.background.paper,
                        color: theme.palette.primary.main,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        transition:
                          'border-color 0.15s ease, box-shadow 0.15s ease',
                        boxShadow: `inset 0 1px 0 ${theme.palette.action.hover}`,
                        fontFamily: theme.typography.fontFamily,
                        userSelect: 'none',
                        cursor: 'move',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        // Single-mode containers let the delete button hang
                        // slightly outside the top-right corner so it doesn't
                        // collide with the component's own header buttons.
                        overflow: isSingleMode ? 'visible' : 'hidden',
                        '&:hover': {
                          borderColor: theme.palette.secondary.main,
                          boxShadow: `0 0 8px ${alpha(theme.palette.secondary.main, 0.4)}, inset 0 1px 0 ${theme.palette.action.hover}`,
                          // Lift this whole tile while hovering so the
                          // outside-the-bounds delete button can overlay
                          // sibling tiles instead of being hidden behind them.
                          ...(isSingleMode && { zIndex: 1_500 }),
                          '& .container-remove-btn, & .container-add-btn': {
                            opacity: 1,
                          },
                        },
                        '&.resizing': {
                          opacity: 0.9,
                          borderColor: theme.palette.warning.main,
                          boxShadow: `0 0 30px ${theme.palette.warning.main}60`,
                        },
                        // Resize handle styling
                        '& > .react-resizable-handle::after': {
                          borderRight: `2px solid ${theme.palette.primary.main} !important`,
                          borderBottom: `2px solid ${theme.palette.primary.main} !important`,
                          width: '7px',
                          height: '7px',
                          filter: `drop-shadow(0 0 4px ${theme.palette.primary.main})`,
                        },
                        '&:hover > .react-resizable-handle::after': {
                          borderColor: `${theme.palette.secondary.main} !important`,
                          filter: `drop-shadow(0 0 6px ${theme.palette.secondary.main})`,
                        },
                        '& > .react-resizable-handle:hover::after': {
                          borderColor: `${theme.palette.warning.main} !important`,
                          filter: `drop-shadow(0 0 8px ${theme.palette.warning.main})`,
                          width: '9px',
                          height: '9px',
                        },
                        // The top-edge ('n') resize handle defaults to a
                        // 20×20 hit area centered at top-center — directly on
                        // top of the tab strip. Shrink it to a thin strip
                        // hugging the very top edge so it stops eating clicks
                        // on the tabs while remaining grabbable.
                        '& > .react-resizable-handle.react-resizable-handle-n':
                          {
                            height: '8px',
                            width: '60px',
                            marginLeft: '-30px',
                            padding: 0,
                            backgroundImage: 'none',
                            '&::after': {
                              top: 1,
                              right: '50%',
                              marginRight: '-3px',
                              width: '6px',
                              height: '6px',
                            },
                          },
                        // Mirror for the bottom-edge handle for visual
                        // symmetry (it doesn't currently overlap anything,
                        // but matching keeps the affordance set consistent).
                        '& > .react-resizable-handle.react-resizable-handle-s':
                          {
                            height: '8px',
                            width: '60px',
                            marginLeft: '-30px',
                            padding: 0,
                            backgroundImage: 'none',
                            '&::after': {
                              bottom: 1,
                              left: '50%',
                              marginLeft: '-3px',
                              width: '6px',
                              height: '6px',
                            },
                          },
                      }}
                    >
                      <IconButton
                        className="container-remove-btn"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveClick(item.i, item);
                        }}
                        sx={{
                          position: 'absolute',
                          top: isSingleMode ? -22 : 4,
                          right: isSingleMode ? 5 : 4,
                          zIndex: isSingleMode ? 1_500 : 10,
                          opacity: 0,
                          transition: 'opacity 0.2s ease-in-out',
                          backgroundColor: isSingleMode
                            ? theme.palette.background.paper
                            : alpha(theme.palette.error.main, 0.1),
                          color: theme.palette.error.main,
                          border: isSingleMode
                            ? `1px solid ${alpha(theme.palette.error.main, 0.5)}`
                            : 'none',
                          boxShadow: isSingleMode
                            ? `0 1px 4px ${alpha(theme.palette.common.black, 0.3)}`
                            : 'none',
                          '&:hover': {
                            opacity: 1,
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.2,
                            ),
                            color: theme.palette.error.dark,
                          },
                          width: 24,
                          height: 24,
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: '14px' }} />
                      </IconButton>

                      {item?.components?.length > 0 ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: isSingleMode ? 0 : 0.5,
                          }}
                        >
                          {!isSingleMode && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1,
                                ),
                              }}
                            >
                              <Tabs
                                value={
                                  activeTabId != null
                                    ? String(activeTabId)
                                    : false
                                }
                                onChange={(_, newValue: string) =>
                                  handleTabChange(item.i, String(newValue))
                                }
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                sx={{
                                  flex: 1,
                                  minHeight: 28,
                                  '& .MuiTabs-flexContainer': {
                                    gap: 0,
                                    minHeight: 28,
                                  },
                                  '& .MuiTabs-indicator': {
                                    display: 'none',
                                  },
                                  '& .MuiTabs-scrollButtons': {
                                    width: 26,
                                    '&.Mui-disabled': {
                                      opacity: 0.3,
                                    },
                                  },
                                  '& .MuiTab-root': {
                                    minHeight: 28,
                                    minWidth: 80,
                                    maxWidth: 'fit-content',
                                    py: 1,
                                    px: 4,
                                    textTransform: 'none',
                                    fontSize: { xs: 10, md: 12 },
                                    fontWeight: 400,
                                    color: theme.palette.text.secondary,
                                    backgroundColor: 'transparent',
                                    transition: 'all 0.2s ease-in-out',
                                    '&.Mui-selected': {
                                      backgroundColor:
                                        theme.palette.background.default,
                                      color: theme.palette.text.primary,
                                      fontWeight: 600,
                                    },
                                    '&:hover': {
                                      backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08,
                                      ),
                                      borderColor: alpha(
                                        theme.palette.primary.main,
                                        0.3,
                                      ),
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    },
                                    '&.Mui-selected:hover': {
                                      backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.25,
                                      ),
                                      borderColor: theme.palette.primary.main,
                                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                                    },
                                  },
                                }}
                              >
                                {item.components.map(
                                  (component: CanvasComponent) => {
                                    const isActive =
                                      activeTabId === component.id;

                                    return (
                                      <Tab
                                        className="canvas-no-drag"
                                        key={component.id}
                                        value={String(component.id)}
                                        label={
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              px: 2.5,
                                              width: '100%',
                                              overflow: 'hidden',
                                            }}
                                          >
                                            <SGTooltip
                                              title={component.label}
                                              placement="top"
                                            >
                                              <Typography
                                                sx={{
                                                  fontSize: { xs: 10, md: 12 },
                                                  fontWeight: isActive
                                                    ? 600
                                                    : 400,
                                                  color: isActive
                                                    ? theme.palette.text.primary
                                                    : theme.palette.text
                                                        .secondary,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                  flex: 1,
                                                }}
                                              >
                                                {component.label}
                                              </Typography>
                                            </SGTooltip>

                                            {isActive && (
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  gap: 0.25,
                                                }}
                                              >
                                                <IconButton
                                                  size="small"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveComponent(
                                                      item.i,
                                                      component.id,
                                                    );
                                                  }}
                                                  sx={{
                                                    p: 0.4,
                                                    color: isActive
                                                      ? theme.palette.text
                                                          .primary
                                                      : theme.palette.text
                                                          .secondary,
                                                    '&:hover': {
                                                      backgroundColor: alpha(
                                                        theme.palette.error
                                                          .main,
                                                        0.1,
                                                      ),
                                                      color:
                                                        theme.palette.error
                                                          .main,
                                                    },
                                                  }}
                                                >
                                                  <CloseIcon
                                                    sx={{ fontSize: '13px' }}
                                                  />
                                                </IconButton>
                                              </Box>
                                            )}
                                          </Box>
                                        }
                                      />
                                    );
                                  },
                                )}
                                <Tab
                                  className="canvas-no-drag"
                                  value="add-component"
                                  disabled={
                                    item.components.length >=
                                    MAX_COMPONENTS_PER_CONTAINER
                                  }
                                  label={
                                    <AddIcon
                                      sx={{
                                        fontSize: { xs: '13px', md: '14px' },
                                        color:
                                          item.components.length >=
                                          MAX_COMPONENTS_PER_CONTAINER
                                            ? theme.palette.text.disabled
                                            : theme.palette.text.primary,
                                        opacity:
                                          item.components.length >=
                                          MAX_COMPONENTS_PER_CONTAINER
                                            ? 0.5
                                            : 1,
                                      }}
                                    />
                                  }
                                  sx={{
                                    minWidth: '40px !important',
                                    '&.Mui-disabled': {
                                      color: theme.palette.text.disabled,
                                      cursor: 'not-allowed',
                                    },
                                  }}
                                />
                              </Tabs>
                            </Box>
                          )}

                          <Box
                            sx={{
                              flex: 1,
                              p: 1,
                              display: 'flex',
                              alignItems: 'stretch',
                              justifyContent: 'stretch',
                              overflow: 'auto',
                              color: theme.palette.text.primary,
                              textShadow: 'none',
                            }}
                          >
                            {(() => {
                              const activeComponent = getActiveComponent(
                                item.i,
                              );
                              if (!activeComponent) {
                                return null;
                              }
                              const isResizingThis =
                                resizingContainerId === item.i;
                              return (
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    position: 'relative',
                                  }}
                                >
                                  {isResizingThis ? (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: alpha(
                                          theme.palette.primary.main,
                                          0.04,
                                        ),
                                        border: `1px dashed ${alpha(
                                          theme.palette.primary.main,
                                          0.3,
                                        )}`,
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: theme.palette.text.secondary,
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        Resizing — release to render
                                      </Typography>
                                    </Box>
                                  ) : (
                                    getActiveComponentValue(
                                      item.i,
                                      activeComponent.id,
                                    )
                                  )}
                                </Box>
                              );
                            })()}
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            gap: 2,
                          }}
                        >
                          <Stack alignItems="center" spacing={0.5}>
                            <Typography
                              variant="body2"
                              sx={{ opacity: 0.7, fontSize: '14px' }}
                            >
                              {isSingleMode
                                ? 'Empty Single-Component Container'
                                : 'Empty Container'}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                opacity: 0.55,
                                fontSize: '11px',
                                textAlign: 'center',
                                maxWidth: 220,
                              }}
                            >
                              {isSingleMode
                                ? 'Pick one component to live here — no tabs.'
                                : `Add up to ${MAX_COMPONENTS_PER_CONTAINER} components and switch between them via tabs.`}
                            </Typography>
                          </Stack>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenComponentDialog(item.i);
                            }}
                            sx={{
                              borderColor: theme.palette.primary.main,
                              color: theme.palette.primary.main,
                              '&:hover': {
                                borderColor: theme.palette.secondary.main,
                                color: theme.palette.secondary.main,
                                backgroundColor: alpha(
                                  theme.palette.secondary.main,
                                  0.1,
                                ),
                              },
                            }}
                          >
                            {isSingleMode ? 'Pick Component' : 'Add Component'}
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </ReactGridLayout>
            </>
          )}
        </div>

        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Container Removal</DialogTitle>
          <DialogContent>
            <Typography>
              This container has{' '}
              {activeWorkspace?.layout.find(
                (item) => item.i === containerToRemove,
              )?.components?.length || 0}{' '}
              component(s). Are you sure you want to remove it? This action
              cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmRemove}
              color="error"
              variant="contained"
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        {(() => {
          const targetContainer = activeWorkspace?.layout.find(
            (item) => item.i === selectedContainer,
          );
          const containerMode = targetContainer?.mode ?? 'tabbed';
          return (
            <AddComponentsDialog
              open={componentDialogOpen}
              onClose={() => {
                setComponentDialogOpen(false);
                setSelectedContainer(null);
              }}
              onConfirm={(selected) =>
                void handleAddSelectedComponents(selected)
              }
              currentCount={targetContainer?.components?.length ?? 0}
              maxComponents={
                containerMode === 'single' ? 1 : MAX_COMPONENTS_PER_CONTAINER
              }
              mode={containerMode}
              workspaceCountByContentId={workspaceCountByContentId}
            />
          );
        })()}

        <ManageGroupsDialog
          open={symbolGroups.manageDialogOpen}
          onClose={symbolGroups.closeManageDialog}
        />
      </Stack>
    </CanvasGroupContext.Provider>
  );
};

export default Canvas;
